import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebaseConfig';
import './EmailVerificationPage.css';

const EmailVerificationPage = () => {
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided');
      return;
    }

    verifyEmail(token);
  }, [location]);

  const verifyEmail = async (token) => {
    try {
      // Call the Firebase function to verify the email
      const verifyEmailToken = httpsCallable(functions, 'verifyEmailToken');
      const result = await verifyEmailToken({ token });

      if (result.data.success) {
        setStatus('success');
        setEmail(result.data.email);
      } else {
        setStatus('error');
        setErrorMessage('Verification failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      setStatus('error');

      // Handle different error types
      switch (error.code) {
        case 'functions/not-found':
          setErrorMessage('Function not found - please contact support');
          break;
        case 'functions/already-exists':
          setErrorMessage('This email has already been verified');
          break;
        case 'functions/deadline-exceeded':
          setErrorMessage('Verification link has expired');
          break;
        case 'functions/invalid-argument':
          setErrorMessage('Invalid verification request');
          break;
        case 'functions/unauthenticated':
          setErrorMessage('Authentication error - please try again');
          break;
        case 'functions/unavailable':
          setErrorMessage('Service temporarily unavailable - please try again later');
          break;
        default:
          setErrorMessage(`Error: ${error.message || 'An error occurred during verification. Please try again.'}`);
      }
    }
  };

  const VerifyingState = () => (
    <div className="verification-container">
      <div className="verification-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="verification-content">
          <div className="spinner"></div>
          <h2>Verifying your email...</h2>
          <p>Please wait while we verify your email address.</p>
        </div>
      </div>
    </div>
  );

  const SuccessState = () => (
    <div className="verification-container">
      <div className="verification-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="verification-content">
          <div className="success-icon">✓</div>
          <h2>Email Verified Successfully!</h2>
          <p>Your email <strong>{email}</strong> has been verified.</p>
          <p>You can now sign in to NextLink with your account.</p>

          <div className="download-section">
            <h3>Download NextLink</h3>
            <div className="download-buttons">
              <a
                href="https://apps.apple.com/app/nextlink"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="app-store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.nextlink"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="google-play-badge"
                />
              </a>
            </div>
          </div>

          <div className="next-steps">
            <p className="small-text">
              Open the NextLink app and sign in with the email address you just verified.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const ErrorState = () => (
    <div className="verification-container">
      <div className="verification-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="verification-content">
          <div className="error-icon">✗</div>
          <h2>Verification Failed</h2>
          <p className="error-message">{errorMessage}</p>

          <div className="error-actions">
            <p>If you're having trouble, please:</p>
            <ul>
              <li>Check that you clicked the correct link from your email</li>
              <li>Make sure the verification link hasn't expired (links expire after 24 hours)</li>
              <li>Try requesting a new verification email in the NextLink app</li>
            </ul>
          </div>

          <div className="download-section">
            <h3>Download NextLink</h3>
            <div className="download-buttons">
              <a
                href="https://apps.apple.com/us/app/the-nextlink/id6743041580"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="app-store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="google-play-badge"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render based on current status
  switch (status) {
    case 'verifying':
      return <VerifyingState />;
    case 'success':
      return <SuccessState />;
    case 'error':
      return <ErrorState />;
    default:
      return <VerifyingState />;
  }
};

export default EmailVerificationPage;